import {
  EXTENSION_URL,
  HOSPITALIZATIONS_COMPLICATIONS,
  HOSPITALIZATIONS_FOLLOW_UP,
  EXTENSIONS_SELF_REPORTED,
  EMERGENCY_VISIT_NOTES,
} from '@constants/extension-codes';

import type { EncounterValues } from '@models/encounter';

import { formatPatientId } from '@utils/format';

import type { EncounterModelInput, Extension } from 'generated/types';
import { EncounterStatus } from 'generated/types';

type EncounterType =
  'emergency' | 'hospitalization' | 'imaging-and-tests' | 'laboratory';

export const ENCOUNTER_MAP: Record<EncounterType, { text: string }> = {
  emergency: { text: 'Emergency Visit' },
  hospitalization: { text: 'Hospitalization' },
  laboratory: { text: 'Laboratory' },
  'imaging-and-tests': { text: 'Imaging and Tests' },
};

export function getEncounterTemplate(
  patient_id: string,
  values: Omit<EncounterValues, 'city' | 'country' | 'facility'>,
  type: EncounterType
): EncounterModelInput {
  const extensions: Extension[] = [];
  if (values.complications && type === 'hospitalization') {
    extensions.push({
      url: `${EXTENSION_URL}${HOSPITALIZATIONS_COMPLICATIONS}`,
      valueString: values.complications,
    });
  }
  if (values.followUp) {
    extensions.push({
      url: `${EXTENSION_URL}${HOSPITALIZATIONS_FOLLOW_UP}`,
      valueString: values.followUp,
    });
  }
  if (values.selfReported) {
    extensions.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }
  if (values.erNotes && type === 'emergency') {
    extensions.push({
      url: `${EXTENSION_URL}${EMERGENCY_VISIT_NOTES}`,
      valueString: values.erNotes,
    });
  }
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extensions.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }

  return {
    resourceType: 'Encounter',
    status: EncounterStatus.planned,
    subject: {
      reference: formatPatientId(patient_id),
    },
    class: {
      code: type,
      display: ENCOUNTER_MAP[type].text,
    },
    ...(values.diagnosis && {
      diagnosis: [
        {
          condition: {
            display: values.diagnosis,
          },
        },
      ],
    }),
    ...((values.visitDurationDays || values.visitDurationUnits) &&
      type === 'hospitalization' && {
        length: {
          value: Number(values.visitDurationDays),
          unit: values.visitDurationUnits,
        },
      }),
    ...(values.practitioner && {
      participant: [
        {
          individual: {
            display: values.practitioner,
          },
          type: [{ coding: [{ code: 'practitioner' }], text: 'Practitioner' }],
        },
      ],
    }),
    ...((values.startDate || values.endDate) && {
      period: {
        ...(values.startDate && {
          start: values.startDate,
        }),
        ...(values.endDate && {
          end: values.endDate,
        }),
      },
    }),
    ...(values.title && {
      reasonCode: [
        {
          text: values.title,
        },
      ],
    }),
    type: [
      {
        coding: [
          {
            code: type,
            display: ENCOUNTER_MAP[type].text,
          },
        ],
      },
    ],
    ...(extensions.length && {
      extension: extensions,
    }),
  };
}
